import React from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';

import { Language, Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ROVER_SEARCH_SOURCE_TYPE_UNKNOWN } from '@rover/constants/src/search/search.constants';
import makeSearchQuery from '@rover/react-lib/src/pages/landing/utilities/makeSearchQuery';
import {
  apiCitiesAndNeighborhoodsList,
  getApiCitiesAndNeighborhoodsListQueryKey,
  useApiCitiesAndNeighborhoodsList,
} from '@rover/rsdk/src/apiClient/latest/api-cities-and-neighborhoods-list';
import {
  apiCountryFooterLinksList,
  getApiCountryFooterLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-footer-links-list';
import {
  apiCountryRegionLinksList,
  getApiCountryRegionLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-region-links-list';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
  useApiFrontendServiceTypesList,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import {
  apiServiceLinksRetrieve,
  getApiServiceLinksRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-service-links-retrieve';
import { ApiSearchOptimizedRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrieveParams';
import { ApiSearchOptimizedRetrievePetType } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrievePetType';
import { ApiServiceLinksRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiServiceLinksRetrieve200';
import { PaginatedBrandLocalityServiceCitiesItemList } from '@rover/rsdk/src/apiClient/latest/model/paginatedBrandLocalityServiceCitiesItemList';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCountryFooterLinksShim from '@rover/shared/js/hooks/useCountryFooterLinksShim';
import useCountryRegionLinksShim from '@rover/shared/js/hooks/useCountryRegionLinksShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useServiceLanding, {
  getServiceLandingFetchArgs,
  type ServiceLandingPathDescriptor,
} from '@rover/shared/js/hooks/useServiceLanding';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import { getPath, getURL } from '@rover/shared/js/utils/getFullUrl';
import getParam from '@rover/shared/js/utils/getParams';
import {
  isServiceLanding,
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';
import {
  type CountryUrlPrefix,
  COUNTRY_URL_PREFIXES_REGEX_PATTERN,
  getCountryUrlPrefix,
  getDefaultShortLanguageByUrlPrefix,
  getShortLanguagesByUrlPrefix,
  US_COUNTRY_URL_PREFIX,
} from '@rover/utilities/roverGeo.constants';
import { redirect } from '@rover/utilities/url';

import { SERVICE_LANDINGS_WITH_SEARCH_RESULTS } from './constants';
import ServiceLandingPage from './ServiceLandingPage';

const EMPTY_SERVICE_LINKS: ApiServiceLinksRetrieve200 = { data: [] };

const SERVICE_LANDING_REGEX = new RegExp(
  `^(?:/(?<country_url_prefix>${COUNTRY_URL_PREFIXES_REGEX_PATTERN})(?:/(?<language_candidate>[a-z]{2}))?)?/(?<service_landing_slug>[a-z-]+)/`
);

export const findLanguageFromPath = (
  path: string,
  countryUrlPrefix: CountryUrlPrefix
): Language | undefined => {
  const candidate = SERVICE_LANDING_REGEX.exec(path)?.groups?.language_candidate;
  if (!candidate) return undefined;
  const validLanguages = getShortLanguagesByUrlPrefix(countryUrlPrefix);
  return validLanguages.includes(candidate as Language) ? (candidate as Language) : undefined;
};

const getCitiesAndNeighborhoods = (
  citiesAndNeighborhoods: PaginatedBrandLocalityServiceCitiesItemList
): Array<{ title: string; href: string }> => {
  return (citiesAndNeighborhoods?.results ?? [])
    .filter((c) => c.city && c.seoSearchPath)
    .map((c) => ({
      title: c.city!,
      href: c.seoSearchPath!,
    }));
};

// Parses the full service landing path into a descriptor. The language is always
// resolved: URL locale wins; the country's default language fills in when absent.
export const parseServiceLandingPath = (path: string): ServiceLandingPathDescriptor => {
  const match = SERVICE_LANDING_REGEX.exec(path);
  const serviceLandingSlug = match?.groups?.service_landing_slug;
  if (!serviceLandingSlug) {
    throw new Error(`Could not parse service landing slug from path: ${path}`);
  }
  const countryUrlPrefix = (getCountryUrlPrefix(path) || US_COUNTRY_URL_PREFIX) as CountryUrlPrefix;
  const urlLanguage = findLanguageFromPath(path, countryUrlPrefix);
  const language = urlLanguage ?? getDefaultShortLanguageByUrlPrefix(countryUrlPrefix);
  return { countryUrlPrefix, serviceLandingSlug, language };
};

const ServiceLandingPageContainer: React.FC & { reactQueryPrefetch: any } = () => {
  const path = getPath();
  // always redirect `/us/*`
  if (getCountryUrlPrefix(path) === US_COUNTRY_URL_PREFIX) {
    // same exact location, just trim off the leading bit
    const location = getURL();
    location.pathname = location.pathname.slice('/us'.length);
    redirect(location);
  }

  const descriptor = parseServiceLandingPath(path);
  const { countryUrlPrefix } = descriptor;

  const { language: locale = Locale.EnUs, settings } = useFrontendConfigurationShim();
  const { serviceLinksForCurrentRoute, serviceLinksWithDisplayName } = useServiceLinksShim();
  const { navigation: countryConfig } = useCountryConfigurationShim();

  const { data: serviceLandingData } = useServiceLanding(descriptor);

  const { results: countryFooterLinks } = useCountryFooterLinksShim();
  const countryRegionLinks = useCountryRegionLinksShim();

  const serviceType = serviceLandingData?.serviceLandingUrlSlug
    ? SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingData.serviceLandingUrlSlug]
    : undefined;

  const { data: citiesAndNeighborhoods } = useApiCitiesAndNeighborhoodsList(
    // API expects the seo slug, not the internal service type (i.e. "pet-sitting" not "overnight-boarding")
    { countryUrlPrefix, serviceType: serviceLandingData?.serviceLandingUrlSlug },
    {
      query: { enabled: !!serviceLandingData?.serviceLandingUrlSlug },
    }
  );

  // Derive the landing type from the LocalizedServiceKeyword-backed serviceLandingData
  // (its url slug shares the ServiceLandingEnum string values) rather than from the
  // serviceLinks routing array, so the page renders even when service links are missing.
  // TODO: drop the ServiceLandingEnum bridge and consume the LSK data directly.
  const serviceLandingType: ServiceLandingEnum | null =
    serviceLandingData && isServiceLanding(serviceLandingData.serviceLandingUrlSlug)
      ? serviceLandingData.serviceLandingUrlSlug
      : null;

  const shouldFetchSearchResults =
    serviceLandingType !== null &&
    !!serviceType &&
    SERVICE_LANDINGS_WITH_SEARCH_RESULTS.includes(serviceLandingType);

  const searchOptimizedRetrieveParams: ApiSearchOptimizedRetrieveParams = {
    serviceType,
    ...(serviceLandingData?.serviceLandingUrlSlug &&
    ['cat-boarding', 'cat-sitting'].includes(serviceLandingData.serviceLandingUrlSlug)
      ? { petType: ApiSearchOptimizedRetrievePetType.cat }
      : {}),
  };

  const { data: searchResults } = useQuery(
    makeSearchQuery(searchOptimizedRetrieveParams, {
      enabled: shouldFetchSearchResults,
      searchSourceType: ROVER_SEARCH_SOURCE_TYPE_UNKNOWN,
    })
  );

  const { data: serviceTypes } = useApiFrontendServiceTypesList();

  return (
    countryConfig &&
    serviceLandingData &&
    citiesAndNeighborhoods?.results &&
    settings &&
    serviceTypes &&
    countryRegionLinks && (
      <ServiceLandingPage
        serviceLandingType={serviceLandingType}
        serviceLinks={serviceLinksForCurrentRoute}
        serviceLinksWithDisplayName={(serviceLinksWithDisplayName ?? []).filter(({ href }) => {
          return (getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX) === countryUrlPrefix;
        })}
        locale={locale}
        countryConfig={countryConfig}
        serviceLandingData={serviceLandingData}
        tags={getParam('tags')}
        citiesAndNeighborhoods={getCitiesAndNeighborhoods(citiesAndNeighborhoods)}
        serviceTypeSlug={
          SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingData.serviceLandingUrlSlug]
        }
        serviceTypes={serviceTypes}
        searchResults={searchResults}
        frontendConfigSettings={settings}
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoods.results}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    )
  );
};

ServiceLandingPageContainer.reactQueryPrefetch = async (queryClient: QueryClient) => {
  const path = getPath();
  const descriptor = parseServiceLandingPath(path);
  const { countryUrlPrefix } = descriptor;

  const serviceLandingRetrieveArgs = getServiceLandingFetchArgs(descriptor);

  // Kick this off before awaiting serviceLandingData below: it has no dependency on that
  // response, so starting it here lets it run concurrently with that fetch instead of
  // serializing behind it.
  const serviceLinksPromise = queryClient.prefetchQuery({
    queryKey: getApiServiceLinksRetrieveQueryKey(),
    queryFn: () => apiServiceLinksRetrieve().catch(() => EMPTY_SERVICE_LINKS),
  });

  const serviceLandingData = await queryClient.fetchQuery(serviceLandingRetrieveArgs);

  return Promise.all([
    serviceLinksPromise,
    queryClient.prefetchQuery({
      queryKey: getApiCitiesAndNeighborhoodsListQueryKey({
        countryUrlPrefix,
        serviceType: serviceLandingData?.serviceLandingUrlSlug,
      }),
      queryFn: () =>
        apiCitiesAndNeighborhoodsList({
          countryUrlPrefix,
          serviceType: serviceLandingData?.serviceLandingUrlSlug,
        }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryFooterLinksListQueryKey({
        countryUrlPrefix,
      }),
      queryFn: () =>
        apiCountryFooterLinksList({
          countryUrlPrefix,
        }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryRegionLinksListQueryKey({
        countryUrlPrefix,
      }),
      queryFn: () =>
        apiCountryRegionLinksList({
          countryUrlPrefix,
        }),
    }),
    // add prefetches for region & country links
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
  ]);
};

export default ServiceLandingPageContainer;
